import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Form, FormGroup, Input, Row } from 'reactstrap';
import crypto from 'crypto-js';

import { UNSUPPORTED_BROWSER_MESSAGE } from '~constants/session';
import { tponeUrl } from '~constants/tpone';

import { useStorage } from '~libs/localstorage';
import { checkProofSupport } from '~libs/sessionKey';

import { Button, ErrorResponse, Link } from '~components';

import { login } from '~main/actions/login';
import { bootstrapSession } from '~main/actions/session';

import {
  selectAuthenticated,
  selectCaptchaReason,
  selectErrorResponse,
  selectLoading,
  selectSessionStatus,
  selectSignOutReason,
} from '~main/selectors/login';

const LOGIN_SECRET = process.env.REACT_APP_LOGIN_AUTHENTICATION_SECRET ?? '';
const REDIRECT_URL = tponeUrl;
const V3_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';
const V2_SITE_KEY = process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY ?? '';

const CAPTCHA_ENABLED = V3_SITE_KEY !== '';
// v2 escalation is only viable when a v2 key is configured; without it there is no checkbox to render,
// so a v3-only deployment must never flip into v2 mode (it would be an unrecoverable lockout).
const CAPTCHA_V2_ENABLED = CAPTCHA_ENABLED && V2_SITE_KEY !== '';

const CAPTCHA_V2_REASONS = new Set(['captcha-required', 'captcha-score-low']);
const FAILED_ATTEMPTS_V2_THRESHOLD = 3;
const V2_VERIFICATION_MESSAGE = 'Additional verification required, please confirm you are human to log in.';
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
const UNVERIFIED_MESSAGE = "We couldn't verify this browser for your session. Please log in again.";

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [, setVisited] = useStorage('dashboard-visited', false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { AES } = crypto;

  const [values, setValues] = useState<{ email: string; password: string }>({ email: '', password: '' });
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [requireV2, setRequireV2] = useState(false);
  const [v2Token, setV2Token] = useState<string | null>(null);
  const [v2Message, setV2Message] = useState<string | null>(null);
  // null until the browser check has answered. Only a definite false blocks sign-in.
  const [proofSupported, setProofSupported] = useState<boolean | null>(null);
  const v2Ref = useRef<ReCAPTCHA>(null);

  const errorResponse = useAppSelector(selectErrorResponse);
  const isAuthenticated = useAppSelector(selectAuthenticated);
  const loading = useAppSelector(selectLoading);
  const captchaReason = useAppSelector(selectCaptchaReason);
  const sessionStatus = useAppSelector(selectSessionStatus);
  const signOutReason = useAppSelector(selectSignOutReason);
  const isLoading = loading === 'pending';

  useEffect(() => {
    // Someone who still has a live session and lands here is forwarded by the effect below.
    if (sessionStatus === 'unknown') dispatch(bootstrapSession());
  }, []);

  useEffect(() => {
    let active = true;
    checkProofSupport().then((supported) => {
      // Only a definite "no" changes the page, so the common case renders nothing extra.
      if (active && !supported) setProofSupported(false);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setVisited(false);
      const target = getTarget();
      navigate(target);
    }
  }, [isAuthenticated]);

  // Escalate to the v2 checkbox when the API signals a low score or that a challenge is required.
  // Dual-trigger by design: this effect reacts to the redux `captchaReason` (server reason), while
  // onSubmit escalates on the local 3-attempt count — neither path can observe the other's signal,
  // so both are needed. Gated on CAPTCHA_V2_ENABLED so a v3-only deployment never enters v2 mode.
  useEffect(() => {
    if (CAPTCHA_V2_ENABLED && captchaReason && CAPTCHA_V2_REASONS.has(captchaReason)) {
      setRequireV2(true);
    }
  }, [captchaReason]);

  const getTarget = () => {
    const { state } = location;
    if (!state) return '/';

    const { from } = state;
    if (from && from !== '/login') return from;
    return '/';
  };

  const onChange = (name: string, value: string) => {
    setValues({
      ...values,
      [name]: value,
    });
  };

  const getV3Token = useCallback(async (): Promise<string> => {
    if (!executeRecaptcha) return '';
    return executeRecaptcha('login');
  }, [executeRecaptcha]);

  // Returns null to signal "do not submit" — a v2 challenge is outstanding but the box is not ticked yet.
  const buildCaptchaParams = async (): Promise<{ recaptchaToken?: string; recaptchaVersion?: 'v3' | 'v2' } | null> => {
    if (!CAPTCHA_ENABLED) return {};

    if (requireV2 && CAPTCHA_V2_ENABLED) {
      // v2 checkbox: the user ticks "I'm not a robot"; onChange sets the token. Block submission until
      // the box is ticked rather than firing an empty-token request the API would reject as captcha-required.
      if (!v2Token) return null;
      return {
        recaptchaToken: v2Token,
        recaptchaVersion: 'v2',
      };
    }

    const token = await getV3Token();
    return {
      recaptchaToken: token,
      recaptchaVersion: 'v3',
    };
  };

  const onSubmit = async (event: FormEvent) => {
    event?.preventDefault();

    const { email, password } = values;
    const captchaParams = await buildCaptchaParams();

    // v2 challenge outstanding (checkbox shown but not ticked): prompt the user instead of
    // submitting an empty token, which the API rejects and which burns a verification attempt.
    if (captchaParams === null) {
      setV2Message(V2_VERIFICATION_MESSAGE);
      return;
    }
    setV2Message(null);

    const result = await dispatch(
      login({
        email,
        password: AES.encrypt(password, LOGIN_SECRET).toString(),
        ...captchaParams,
      }),
    );

    if (login.rejected.match(result)) {
      const newCount = failedAttempts + 1;
      setFailedAttempts(newCount);

      const reason = result.payload?.reason;
      // Only escalate to v2 when a v2 key is configured — otherwise there is no checkbox to render.
      if (
        CAPTCHA_V2_ENABLED &&
        (newCount >= FAILED_ATTEMPTS_V2_THRESHOLD || (reason && CAPTCHA_V2_REASONS.has(reason)))
      ) {
        setRequireV2(true);
      }

      // Reset the v2 checkbox so a fresh tick is required on retry
      v2Ref.current?.reset();
      setV2Token(null);
    }
  };

  return (
    <div className="justify-content-center">
      <Row>
        <Col className="login d-flex justify-content-center mt-5">
          <Card className="rounded">
            <CardHeader className="d-flex justify-content-center bg-menu-background">
              <img src="/assets/primary_logo.png" alt="Logo" />
            </CardHeader>
            <CardBody className="d-flex flex-column text-center">
              <h3 data-cy={`login-header`}>Login</h3>
              {signOutReason === 'expired' && (
                <ErrorResponse message={SESSION_EXPIRED_MESSAGE} dataCy="sessionExpiredMessage" />
              )}
              {signOutReason === 'unverified' && (
                <ErrorResponse message={UNVERIFIED_MESSAGE} dataCy="sessionUnverifiedMessage" />
              )}
              {proofSupported === false && (
                <ErrorResponse message={UNSUPPORTED_BROWSER_MESSAGE} dataCy="proofUnsupportedMessage" />
              )}
              <ErrorResponse message={errorResponse} dataCy="errorResponse" />
              <Form name="login-form" onSubmit={onSubmit}>
                <FormGroup>
                  <Input
                    type="text"
                    id="email"
                    name="email"
                    value={values.email}
                    placeholder="Email Address"
                    onChange={({ target: { name, value } }) => onChange(name, value)}
                  />
                </FormGroup>
                <FormGroup>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    value={values.password}
                    placeholder="Password"
                    onChange={({ target: { name, value } }) => onChange(name, value)}
                  />
                </FormGroup>
                {requireV2 && V2_SITE_KEY && (
                  <FormGroup className="d-flex justify-content-center">
                    <ReCAPTCHA
                      ref={v2Ref}
                      sitekey={V2_SITE_KEY}
                      onChange={(token) => {
                        setV2Token(token);
                        if (token) setV2Message(null);
                      }}
                    />
                  </FormGroup>
                )}
                <ErrorResponse message={v2Message} dataCy="captchaMessage" />
                <Button
                  className="w-25"
                  color="success"
                  type="submit"
                  loading={isLoading}
                  disabled={proofSupported === false}
                >
                  Login
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col className="login d-flex justify-content-center">
          <Card className="rounded mt-3 justify-content-center">
            <CardHeader className="text-white">Forgot Password / PIN?</CardHeader>
            <CardBody className="d-flex justify-content-center">
              <Link to={`${REDIRECT_URL}/login.asp`}>
                <Button color="warning">Reset Password</Button>
              </Link>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Login;
